import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
    {children}
  </section>
);

const P = ({ children }) => (
  <p className="text-gray-700 leading-relaxed mb-3">{children}</p>
);

const CHECKBOX_LABEL =
  "I would like to receive recurring SMS/text message updates from FieldHouse about the organizations and teams with which I (or my player) am registered or assigned, including schedule changes, event reminders, and announcements. Message frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to cancel. This consent is optional and is not a condition of registering for or using FieldHouse.";

const OPT_IN_MESSAGE =
  'FieldHouse: You are subscribed to recurring SMS updates for organizations and teams with which you or your player are registered or assigned. Message frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to cancel.';

const HELP_MESSAGE =
  'FieldHouse: For help visit https://www.fieldhouse.club/support or email support@fieldhouse.club. Msg & data rates may apply. Reply STOP to cancel.';

const STOP_MESSAGE =
  'FieldHouse: You have successfully unsubscribed and will receive no further messages. Reply START to resubscribe.';

const MessageSample = ({ children }) => (
  <div className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm font-mono mb-3">{children}</div>
);

const SmsCompliance = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">SMS Compliance</h1>
          <P>
            This page explains how FieldHouse collects consent for SMS/text messages, exactly what we
            say when you opt in, and how to get help or stop messages at any time.
          </P>

          <Section title="How SMS consent works">
            <P>
              FieldHouse only sends recurring SMS updates to a mobile number after the person
              associated with that number has affirmatively opted in — either by checking the consent
              checkbox on our <Link to="/sms-opt-in" className="text-blue-600 underline">SMS Opt-In</Link> page,
              or by enabling SMS notifications in their account settings inside the app. The consent
              checkbox is unchecked by default, is presented separately from account creation and from
              agreeing to our Terms of Service, and is optional: choosing not to receive SMS updates has
              no effect on your ability to register for or use FieldHouse.
            </P>
            <P>
              After opting in, the number receives an immediate opt-in confirmation text (shown below).
              Recipients can reply <strong>HELP</strong> for help or <strong>STOP</strong> at any time
              to immediately stop all further messages, and <strong>START</strong> to resubscribe.
            </P>
          </Section>

          <Section title="Screenshot of the opt-in form">
            <img
              src="/sms-opt-in-screenshot.png"
              alt="FieldHouse SMS opt-in form showing the mobile number field and the unchecked SMS consent checkbox with its disclosure language"
              className="w-full rounded-lg border border-gray-200 mb-2"
            />
            <P>
              Live version: <Link to="/sms-opt-in" className="text-blue-600 underline">fieldhouse.club/sms-opt-in</Link>
            </P>
          </Section>

          <Section title="Exact checkbox language">
            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">{CHECKBOX_LABEL}</div>
          </Section>

          <Section title="Message samples">
            <p className="font-medium text-gray-900 mb-1">Opt-in confirmation (sent immediately after signup)</p>
            <MessageSample>{OPT_IN_MESSAGE}</MessageSample>
            <p className="font-medium text-gray-900 mb-1">Reply HELP</p>
            <MessageSample>{HELP_MESSAGE}</MessageSample>
            <p className="font-medium text-gray-900 mb-1">Reply STOP</p>
            <MessageSample>{STOP_MESSAGE}</MessageSample>
          </Section>

          <Section title="Your data">
            <P>
              Mobile numbers and SMS consent are never sold or shared with third parties for marketing.
              See our <Link to="/privacy-policy" className="text-blue-600 underline">Privacy Policy</Link>{' '}
              and <Link to="/terms-of-service" className="text-blue-600 underline">Terms of Service</Link>{' '}
              for full details.
            </P>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default SmsCompliance;
